import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    TextField, Typography, Box, CircularProgress, Grid, Card,
    CardContent, CardMedia, CardActionArea, Chip, Pagination
} from '@mui/material';
import { useMemeTemplates } from '../context/MemeTemplateContext';
import { MemeTemplate } from '../types';

const ITEMS_PER_PAGE = 50;

const MemeLibrary = () => {
    const { filteredTemplates, loading, error, searchTerm, setSearchTerm } = useMemeTemplates();
    const navigate = useNavigate();

    // Pagination state
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPageItems, setCurrentPageItems] = useState<MemeTemplate[]>([]);

    // Update total pages when filtered templates change
    useEffect(() => {
        setTotalPages(Math.ceil(filteredTemplates.length / ITEMS_PER_PAGE));
        // Reset to first page when filter changes
        if (page !== 1) {
            setPage(1);
        } else {
            // Update current page items
            updateCurrentPageItems(1, filteredTemplates);
        }
    }, [filteredTemplates]);

    // Update current page items when page changes
    useEffect(() => {
        updateCurrentPageItems(page, filteredTemplates);
        // Scroll to top when page changes
        window.scrollTo(0, 0);
    }, [page, filteredTemplates]);

    const updateCurrentPageItems = (currentPage: number, templates: MemeTemplate[]) => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        setCurrentPageItems(templates.slice(startIndex, endIndex));
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const handleSelectTemplate = (id: string) => {
        navigate(`/editor/${id}`);
    };

    if (loading) {
        return (
            <Box className="flex justify-center items-center h-64">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box className="text-center py-8">
                <Typography variant="h6" color="error">
                    {error}
                </Typography>
            </Box>
        );
    }

    return (
        <div>
            <Box className="mb-6">
                <Typography variant="h4" component="h1" className="mb-4 text-white">
                    Meme Template Library
                </Typography>
                <TextField
                    fullWidth
                    variant="outlined"
                    label="Search templates by name or tags"
                    value={searchTerm}
                    onChange={handleSearch}
                    className="bg-gray-800 rounded-lg"
                    InputProps={{
                        className: 'text-white',
                    }}
                    InputLabelProps={{
                        className: 'text-gray-400',
                    }}
                />
            </Box>

            {filteredTemplates.length === 0 ? (
                <Typography className="text-center py-8 text-gray-400">
                    No templates found. Try a different search term.
                </Typography>
            ) : (
                <>
                    {/* Pagination Info */}
                    <Box className="flex justify-between items-center mb-4">
                        <Typography className="text-gray-400">
                            Showing {currentPageItems.length} of {filteredTemplates.length} templates
                        </Typography>

                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={handlePageChange}
                            color="primary"
                            size="large"
                            className="mt-4"
                        />
                    </Box>

                    {/* Template Grid */}
                    <Grid container spacing={3}>
                        {currentPageItems.map((template) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={template.id}>
                                <Card className="bg-gray-800 hover:bg-gray-700 transition-colors">
                                    <CardActionArea onClick={() => handleSelectTemplate(template.id)}>
                                        <CardMedia
                                            component="img"
                                            height="200"
                                            image={template.imageUrl}
                                            alt={template.name}
                                            className="object-contain bg-gray-900"
                                        />
                                        <CardContent>
                                            <Typography variant="h6" component="div" className="text-white mb-2">
                                                {template.name}
                                            </Typography>
                                            <Box className="flex flex-wrap gap-1">
                                                {template.tags.slice(0, 3).map((tag) => (
                                                    <Chip
                                                        key={tag}
                                                        label={tag}
                                                        size="small"
                                                        className="bg-indigo-700 text-white"
                                                    />
                                                ))}
                                                {template.tags.length > 3 && (
                                                    <Chip
                                                        label={`+${template.tags.length - 3}`}
                                                        size="small"
                                                        className="bg-gray-600 text-white"
                                                    />
                                                )}
                                            </Box>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Bottom Pagination */}
                    <Box className="flex justify-center mt-6 mb-4">
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={handlePageChange}
                            color="primary"
                            size="large"
                        />
                    </Box>
                </>
            )}
        </div>
    );
};

export default MemeLibrary;